import { useState } from "react";
import { MessageCircle, Sparkles } from "lucide-react";
import { OwlMascot } from "../components/OwlMascot";

/**
 * "Chat with Owl" — the on-page entry into the conversation. The heavy chat
 * logic lives in ChatDrawer; this section is the invitation: a prominent
 * composer + a few starter questions that open the drawer pre-filled.
 *
 * Owl's chat answers questions about NYVO fund ratings + the market. It does
 * not give personal buy/sell advice — that's by design.
 */

const STARTERS = [
  "How does Owl rate mutual funds?",
  "Is my large-cap fund any good?",
  // 2026-07-06 (founder call): compare lives IN chat — the on-page grid is gone.
  "Compare two funds for me",
  "What's happening in the market today?",
  "Explain SIP like I'm new to this",
];

export function ChatWithOwl({
  onOpenChat,
}: {
  onOpenChat: (prefill?: string) => void;
}) {
  const [text, setText] = useState("");

  function submit() {
    const q = text.trim();
    onOpenChat(q.length > 0 ? q : undefined);
    setText("");
  }

  return (
    <section id="chat" className="mt-14">
      <div className="card overflow-hidden p-0">
        <div className="flex flex-col gap-5 p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <OwlMascot size="card" />
            <div>
              <h2 className="font-display text-xl font-semibold text-nyvo-900 sm:text-2xl">
                Chat with Owl
              </h2>
              <p className="text-sm text-slate-500">
                Ask about a fund, a rating, or what's going on in the market.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 shadow-xs focus-within:border-sky-300">
            <MessageCircle className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
              placeholder="Ask Owl anything…"
              aria-label="Ask Owl a question"
              className="min-w-0 flex-1 bg-transparent text-sm text-nyvo-900 outline-hidden placeholder:text-slate-400"
            />
            <button
              type="button"
              onClick={submit}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sky-500 px-4 py-1.5 text-xs font-bold text-white transition hover:bg-sky-600"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              Ask
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {STARTERS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => onOpenChat(q)}
                className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 transition hover:border-sky-300 hover:text-sky-700"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
